import React, { useState, type ReactNode } from "react";
import styled from "@emotion/styled";
import type { TooltipDatum, TooltipState } from "../types/atlasTooltip";
import { useIsolatedTooltipPayload } from "../hooks/TooltipStateIsolationProvider";
import type {
  TooltipPosition,
  TooltipTheme,
} from "./tooltip/SharedTooltipComponents";
import TooltipPortal from "./TooltipPortal";

// display: contents — transparent in layout so it doesn't affect SVG/chart positioning
const MouseCaptureWrapper = styled.span`
  display: contents;
`;

// ── Types ────────────────────────────────────────────────────────────

export interface TooltipStateBridgeProps<
  T extends TooltipDatum = TooltipDatum,
> {
  /** Chart content — rendered inside a mouse-tracking wrapper. */
  children: ReactNode;
  /**
   * Tooltip body as a memo() component that reads state via
   * `useIsolatedTooltipPayload()`. Preferred over `renderExplanation`.
   */
  tooltipContent?: ReactNode;
  /** @deprecated Use `tooltipContent` — a memo() component that reads state via useIsolatedTooltipPayload(). */
  renderExplanation?: (
    tooltipState: TooltipState<T> | undefined,
  ) => ReactNode;
  /**
   * When true (default), `isFixed`, `isFixedCoords`, and `fillColor` fall
   * back to values from the isolated tooltip state if not set explicitly.
   */
  deriveTooltipPropsFromState?: boolean;

  // Portal configuration
  cursor?: string;
  overrideStyles?: boolean;
  /** When true, tooltip width sizes to content instead of using a fixed baseline width. */
  flexWidth?: boolean;
  theme?: TooltipTheme;
  tooltipPosition?: TooltipPosition;
  snapToPosition?: boolean;
  /** Direction to snap tooltip relative to data point (`"above"` | `"right"`). */
  snapDirection?: "above" | "right";
  clearHighlightedItem?: () => void;

  /**
   * DOM element ID for the portal container.
   * Falls back to `document.body` when omitted.
   */
  portalContainerId?: string;

  // Explicit tooltip state overrides
  isFixed?: boolean;
  isFixedCoords?: [number, number];
  fillColor?: string;
  onFixedChange?: (isFixed: boolean) => void;
  persistOnMouseLeave?: boolean;
}

// ── Component ────────────────────────────────────────────────────────

/**
 * Subscribes to {@link useIsolatedTooltipPayload} and renders a positioned
 * tooltip portal alongside chart `children`.
 *
 * Children are rendered inside a `display: contents` wrapper that captures
 * mouse moves for hover tooltip positioning.  Place a memoized chart as a
 * child; charts should use {@link useTooltipDispatchRef} for hover handlers
 * so they do not rerender when the payload changes.
 */
export function TooltipStateBridge<T extends TooltipDatum = TooltipDatum>({
  children,
  tooltipContent,
  renderExplanation,
  deriveTooltipPropsFromState = true,
  cursor,
  overrideStyles,
  flexWidth,
  theme,
  tooltipPosition,
  snapToPosition,
  snapDirection,
  clearHighlightedItem,
  portalContainerId,
  isFixed: isFixedProp,
  isFixedCoords: isFixedCoordsProp,
  onFixedChange,
  persistOnMouseLeave,
}: TooltipStateBridgeProps<T>) {
  const tooltipState = useIsolatedTooltipPayload<T>();
  const explanation = tooltipContent
    ? (tooltipState?.contentData ? tooltipContent : null)
    : (renderExplanation ? renderExplanation(tooltipState) : null);

  const [mouseCoords, setMouseCoords] = useState({ top: 0, left: 0 });

  // Merge explicit props with isolated tooltip state
  const isFixed = deriveTooltipPropsFromState
    ? (isFixedProp ?? tooltipState?.isFixed ?? false)
    : (isFixedProp ?? false);
  const isFixedCoords = deriveTooltipPropsFromState
    ? (isFixedCoordsProp ?? tooltipState?.isFixedCoords)
    : isFixedCoordsProp;

  // Use fixed coords when pinned/snapped, mouse coords when free-hovering
  const coords =
    isFixedCoords !== undefined
      ? { top: isFixedCoords[1], left: isFixedCoords[0] }
      : mouseCoords;

  const handleMouseMoveCapture = (e: React.MouseEvent) => {
    if (isFixed) {
      // When fixed, stop propagation so chart children don't update content
      e.stopPropagation();
    } else if (isFixedCoords === undefined) {
      // Free-hover: track cursor position
      setMouseCoords({ top: e.clientY, left: e.clientX });
    }
  };

  const handleClose = () => {
    clearHighlightedItem?.();
  };

  return (
    <MouseCaptureWrapper
      onMouseMoveCapture={handleMouseMoveCapture}
      style={{ cursor }}
    >
      {children}
      <TooltipPortal
        explanation={explanation}
        coords={coords}
        isFixed={isFixed}
        theme={theme}
        tooltipPosition={tooltipPosition}
        overrideStyles={overrideStyles}
        flexWidth={flexWidth}
        snapToPosition={snapToPosition}
        snapDirection={snapDirection}
        onClose={handleClose}
        portalContainerId={portalContainerId}
      />
    </MouseCaptureWrapper>
  );
}
